export const DEFAULT_MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

export const BASE_TABLE_COLUMNS = [{ Header: "#", accessor: "label" }];

const METRIC_DEFINITIONS = [
    {
        label: "Clients Serviced",
        keys: ["clientsServiced", "ClientsServiced", "clients_serviced"],
    },
    {
        label: "Clients in Red",
        keys: ["clientsInRed", "ClientsInRed", "clients_in_red"],
    },
    {
        label: "Clients that Shut Down",
        keys: [
            "clientsThatShutDown",
            "clientsShutDown",
            "ClientsThatShutDown",
            "clients_that_shut_down",
            "cleintsThatShutDown",
        ],
    },
];

const MONTH_KEY_ALIASES = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
];

export const buildMonthAccessor = (index) => `month_${index}`;

export const normalizeMonthLabels = (months = []) => {
    if (!Array.isArray(months) || !months.length) {
        return [...DEFAULT_MONTHS];
    }

    return months
        .map((item) => {
            if (typeof item === "string" || typeof item === "number") {
                return String(item).trim();
            }
            return String(item?.label ?? item?.month ?? "").trim();
        })
        .filter(Boolean);
};

export const buildTableColumns = (months = []) => {
    const monthColumns = normalizeMonthLabels(months).map((month, index) => ({
        Header: month,
        accessor: buildMonthAccessor(index),
        isMonthValue: true,
    }));

    return [...BASE_TABLE_COLUMNS, ...monthColumns];
};

const formatMetricCell = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed === 0) return "";
    return String(value);
};

const formatCellCount = (count) => {
    if (count === null || count === undefined || count === "") return "";
    const parsed = Number(count);
    if (!Number.isFinite(parsed)) return String(count);
    if (parsed === 0) return "";
    return String(parsed);
};

const buildMonthCellData = (cell = {}) => {
    const drillDownKey = cell?.drillDownKey ? String(cell.drillDownKey).trim() : "";
    const display = formatCellCount(cell?.count);

    return {
        display,
        drillDownKey: drillDownKey || null,
        isClickable: Boolean(drillDownKey && display),
    };
};

const normalizeMonthArray = (values = [], monthCount = 12) =>
    Array.from({ length: monthCount }, (_, index) =>
        formatMetricCell(Array.isArray(values) ? values[index] : "")
    );

const normalizeMonthObject = (valueObject = {}, monthCount = 12) =>
    Array.from({ length: monthCount }, (_, index) => {
        const monthLabel = DEFAULT_MONTHS[index];
        const aliases = [
            monthLabel,
            monthLabel.toLowerCase(),
            MONTH_KEY_ALIASES[index],
            String(index + 1),
            `month${index + 1}`,
            `m${index + 1}`,
        ];
        for (const alias of aliases) {
            if (valueObject?.[alias] !== undefined && valueObject?.[alias] !== null) {
                return formatMetricCell(valueObject[alias]);
            }
        }
        return "";
    });

const pickMonthlyValues = (source = {}, keys = [], monthCount = 12) => {
    for (const key of keys) {
        const rawValue = source?.[key];
        if (Array.isArray(rawValue)) {
            return normalizeMonthArray(rawValue, monthCount);
        }
        if (rawValue && typeof rawValue === "object") {
            return normalizeMonthObject(rawValue, monthCount);
        }
    }
    return Array(monthCount).fill("");
};

const resolveMonthIndex = (monthValue, monthDefinitions = [], monthCount = 12) => {
    const numericMonth = Number(monthValue);
    if (Number.isFinite(numericMonth) && numericMonth >= 1 && numericMonth <= monthCount) {
        return numericMonth - 1;
    }

    const normalized = String(monthValue ?? "").trim().toLowerCase();
    if (!normalized) return -1;

    return (Array.isArray(monthDefinitions) ? monthDefinitions : []).findIndex((item) => {
        if (typeof item === "string" || typeof item === "number") {
            return String(item).trim().toLowerCase() === normalized;
        }
        const label = String(item?.label ?? "").trim().toLowerCase();
        const monthNumber = String(item?.month ?? "").trim();
        return label === normalized || monthNumber === normalized;
    });
};

const mapMonthlyCells = (cells = [], monthDefinitions = [], monthCount = 12) => {
    const values = Array(monthCount).fill("");

    (Array.isArray(cells) ? cells : []).forEach((cell = {}) => {
        const monthIndex = resolveMonthIndex(
            cell?.month ?? cell?.monthNumber ?? cell?.monthIndex,
            monthDefinitions,
            monthCount
        );
        if (monthIndex < 0 || monthIndex >= monthCount) return;

        values[monthIndex] = formatMetricCell(
            cell?.value ??
            cell?.count ??
            cell?.displayValue ??
            cell?.total ??
            ""
        );
    });

    return values;
};

const mapStatusRowCellsByIndex = (cells = [], monthCount = 12) =>
    Array.from({ length: monthCount }, (_, index) => {
        const cell = Array.isArray(cells) ? cells[index] : null;
        if (!cell || typeof cell !== "object") {
            return { display: "", drillDownKey: null, isClickable: false };
        }
        return buildMonthCellData(cell);
    });

const mapLegacyMonthValues = (statusRow = {}, monthDefinitions = [], monthCount = 12) => {
    const arrayCandidates = [
        statusRow?.values,
        statusRow?.monthlyValues,
        statusRow?.counts,
        statusRow?.monthlyCounts,
        statusRow?.data,
    ];

    for (const candidate of arrayCandidates) {
        if (Array.isArray(candidate)) {
            return normalizeMonthArray(candidate, monthCount).map((display) => ({
                display,
                drillDownKey: null,
                isClickable: false,
            }));
        }
    }

    if (Array.isArray(statusRow?.months)) {
        return mapMonthlyCells(statusRow.months, monthDefinitions, monthCount).map((display) => ({
            display,
            drillDownKey: null,
            isClickable: false,
        }));
    }

    if (statusRow?.monthValues && typeof statusRow.monthValues === "object") {
        return normalizeMonthObject(statusRow.monthValues, monthCount).map((display) => ({
            display,
            drillDownKey: null,
            isClickable: false,
        }));
    }

    return Array.from({ length: monthCount }, () => ({
        display: "",
        drillDownKey: null,
        isClickable: false,
    }));
};

const mapStatusRowMonthCells = (statusRow = {}, monthDefinitions = [], monthCount = 12) => {
    if (Array.isArray(statusRow?.cells)) {
        return mapStatusRowCellsByIndex(statusRow.cells, monthCount);
    }

    return mapLegacyMonthValues(statusRow, monthDefinitions, monthCount);
};

const resolveUserDisplayName = (user = {}) =>
    user?.userName ??
    user?.name ??
    user?.user ??
    user?.smeName ??
    user?.callerName ??
    "";

const mapMetricsFromUser = (user = {}, monthDefinitions = [], monthCount = 12) => {
    if (Array.isArray(user?.statusRows) && user.statusRows.length) {
        return user.statusRows.map((statusRow = {}) => ({
            label: String(statusRow?.status ?? statusRow?.label ?? "").trim(),
            statusKey: String(statusRow?.statusKey ?? "").trim(),
            monthCells: mapStatusRowMonthCells(statusRow, monthDefinitions, monthCount),
        }));
    }

    if (Array.isArray(user?.metrics) && user.metrics.length) {
        return user.metrics.map((metric = {}) => {
            const values = Array.isArray(metric?.monthlyValues)
                ? normalizeMonthArray(metric.monthlyValues, monthCount)
                : Array.isArray(metric?.values)
                    ? normalizeMonthArray(metric.values, monthCount)
                    : Array(monthCount).fill("");

            return {
                label: String(metric?.label ?? metric?.name ?? "").trim(),
                monthCells: values.map((display) => ({
                    display,
                    drillDownKey: null,
                    isClickable: false,
                })),
            };
        });
    }

    return METRIC_DEFINITIONS.map((definition) => ({
        label: definition.label,
        monthCells: pickMonthlyValues(user, definition.keys, monthCount).map((display) => ({
            display,
            drillDownKey: null,
            isClickable: false,
        })),
    }));
};

export const mapUsersToTableRows = (users = [], months = []) => {
    const monthLabels = normalizeMonthLabels(months);
    const monthCount = monthLabels.length;
    const rows = [];

    (Array.isArray(users) ? users : []).forEach((user = {}, userIndex) => {
        const userId = user?.id ?? user?.userId ?? `user-${userIndex}`;
        const metrics = mapMetricsFromUser(user, months, monthCount);
        const userName = resolveUserDisplayName(user);

        rows.push({
            id: `${userId}-header`,
            isUserHeaderRow: true,
            label: userName,
        });

        metrics.forEach((metric = {}, metricIndex) => {
            const row = {
                id: `${userId}-metric-${metricIndex}`,
                label: metric?.label || "",
                statusKey: metric?.statusKey || "",
                userId,
                userName,
            };

            monthLabels.forEach((_, monthIndex) => {
                row[buildMonthAccessor(monthIndex)] = metric?.monthCells?.[monthIndex] ?? {
                    display: "",
                    drillDownKey: null,
                    isClickable: false,
                };
            });

            rows.push(row);
        });

        if (userIndex < users.length - 1) {
            rows.push({
                id: `${userId}-gap`,
                isSectionGap: true,
            });
        }
    });

    return rows;
};

export const extractReportPayload = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data &&
        typeof responseData.data === "object" &&
        !Array.isArray(responseData.data)
            ? responseData.data
            : responseData;

    const months =
        Array.isArray(nestedData?.months) && nestedData.months.length
            ? nestedData.months
            : DEFAULT_MONTHS;

    const users = Array.isArray(nestedData?.users)
        ? nestedData.users
        : Array.isArray(nestedData?.callers)
            ? nestedData.callers
            : Array.isArray(nestedData?.rows)
                ? nestedData.rows
                : Array.isArray(nestedData?.records)
                    ? nestedData.records
                    : Array.isArray(responseData)
                        ? responseData
                        : [];

    const drillDowns = Array.isArray(nestedData?.drillDowns) ? nestedData.drillDowns : [];

    return { months, users, drillDowns };
};

export const buildDrillDownMap = (drillDowns = []) => {
    const map = new Map();

    if (Array.isArray(drillDowns)) {
        drillDowns.forEach((entry = {}) => {
            const key = String(
                entry?.drillDownKey ?? entry?.key ?? entry?.id ?? ""
            ).trim();
            if (key) {
                map.set(key, entry);
            }
        });
        return map;
    }

    if (drillDowns && typeof drillDowns === "object") {
        Object.entries(drillDowns).forEach(([key, entry]) => {
            const normalizedKey = String(key ?? "").trim();
            if (normalizedKey) {
                map.set(normalizedKey, entry);
            }
        });
    }

    return map;
};

export const getDrillDownItems = (entry = {}) => {
    const list =
        entry?.clients ??
        entry?.items ??
        entry?.records ??
        entry?.rows ??
        entry?.candidates ??
        [];
    return Array.isArray(list) ? list : [];
};
